import { useState, useRef } from 'react';
import { Mic, Square, Play, Trash2, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { disguiseVoice, encodeOwnVoice, VOICE_PRESET_NAMES } from '../lib/audioDisguise';

interface VoiceRecorderProps {
  // Called with the public URL once recording, disguising, and upload
  // all finish successfully.
  onRecorded: (audioUrl: string) => void;
  // Folder prefix inside the 'voice-notes' bucket, e.g. a test id.
  pathPrefix: string;
  // Whether to run the recording through the voice disguise + gender
  // prompt. Default true (the anonymous question-sender's flow). Set to
  // false for the person answering — they've tapped a link to answer a
  // question addressed to them, so their voice should come through as
  // their own real voice, not disguised.
  disguise?: boolean;
}

type RecorderState = 'idle' | 'consent' | 'recording' | 'processing' | 'done';

export default function VoiceRecorder({ onRecorded, pathPrefix, disguise = true }: VoiceRecorderProps) {
  const [state, setState] = useState<RecorderState>('idle');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [seconds, setSeconds] = useState(0);
  // null = let disguiseVoice pick randomly ("Surprise me").
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startRecording = async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        handleRecordingComplete();
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setState('recording');
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (err) {
      setError('Could not access microphone. Check your browser permissions.');
    }
  };

  const stopRecording = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    mediaRecorderRef.current?.stop();
  };

  const handleRecordingComplete = async () => {
    setState('processing');
    try {
      const rawBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
      const processedBlob = disguise
        ? await disguiseVoice(rawBlob, selectedPreset ?? undefined)
        : await encodeOwnVoice(rawBlob);

      setPreviewUrl(URL.createObjectURL(processedBlob));

      const supabaseClient = supabase;
      const fileName = `${pathPrefix}/${Date.now()}.wav`;

      const { error: uploadError } = await supabaseClient.storage
        .from('voice-notes')
        .upload(fileName, processedBlob, { contentType: 'audio/wav' });

      if (uploadError) throw uploadError;

      const { data } = supabaseClient.storage.from('voice-notes').getPublicUrl(fileName);

      setState('done');
      onRecorded(data.publicUrl);
    } catch (err: any) {
      console.error('Voice recording error:', err);
      const detail = err?.message || err?.error_description || String(err);
      setError(`Something went wrong processing your recording: ${detail}`);
      setState('idle');
    }
  };

  const reset = () => {
    setPreviewUrl(null);
    setState('idle');
    setSeconds(0);
    setError(null);
    setSelectedPreset(null);
  };

  return (
    <div className="flex flex-col gap-2">
      {state === 'idle' && (
        <button
          type="button"
          onClick={() => (disguise ? setState('consent') : startRecording())}
          className="flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors text-sm font-medium"
          style={{ borderColor: 'var(--color-brand-border)', color: 'var(--color-brand-text)', background: 'var(--color-brand-card)' }}
        >
          <Mic size={18} />
          {disguise ? 'Record a voice message instead' : 'Record your answer'}
        </button>
      )}

      {state === 'consent' && (
        <div className="p-4 rounded-lg border flex flex-col gap-3" style={{ borderColor: 'var(--color-brand-border)', background: 'var(--color-brand-card)' }}>
          <p className="text-sm" style={{ color: 'var(--color-brand-text)', opacity: 0.85 }}>
            We'll disguise your voice before sending, so it can't be
            recognized. Your phone will ask for microphone permission next.
          </p>

          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium" style={{ color: 'var(--color-brand-text)', opacity: 0.6 }}>Voice</span>
            <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto pr-1">
              <button
                type="button"
                onClick={() => setSelectedPreset(null)}
                className="px-3 py-1.5 rounded-full text-xs font-medium border transition-colors"
                style={selectedPreset === null
                  ? { background: '#dc2626', borderColor: '#dc2626', color: '#fff' }
                  : { background: 'var(--color-brand-bg)', borderColor: 'var(--color-brand-border)', color: 'var(--color-brand-text)' }}
              >
                Surprise me
              </button>
              {VOICE_PRESET_NAMES.map((name) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => setSelectedPreset(name)}
                  className="px-3 py-1.5 rounded-full text-xs font-medium border transition-colors"
                  style={selectedPreset === name
                    ? { background: '#dc2626', borderColor: '#dc2626', color: '#fff' }
                    : { background: 'var(--color-brand-bg)', borderColor: 'var(--color-brand-border)', color: 'var(--color-brand-text)' }}
                >
                  {name}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={startRecording}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
            >
              <Mic size={16} />
              Continue
            </button>
            <button
              type="button"
              onClick={reset}
              className="px-4 py-2 rounded-lg border text-sm font-medium transition-colors"
              style={{ borderColor: 'var(--color-brand-border)', color: 'var(--color-brand-text)' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {state === 'recording' && (
        <button
          type="button"
          onClick={stopRecording}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors text-sm font-medium animate-pulse"
        >
          <Square size={16} />
          Recording... {seconds}s (tap to stop)
        </button>
      )}

      {state === 'processing' && (
        <div className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm" style={{ background: 'var(--color-brand-card)', color: 'var(--color-brand-text)', opacity: 0.75 }}>
          <Loader2 size={18} className="animate-spin" />
          Disguising your voice...
        </div>
      )}

      {state === 'done' && previewUrl && (
        <div className="flex items-center gap-2">
          <audio src={previewUrl} controls className="h-9 flex-1" />
          <button
            type="button"
            onClick={reset}
            className="p-2 rounded-lg border transition-colors"
            style={{ borderColor: 'var(--color-brand-border)', color: 'var(--color-brand-text)' }}
            aria-label="Delete recording"
          >
            <Trash2 size={16} />
          </button>
        </div>
      )}

      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  );
}
